import React from 'react';
import Button from '../ui/Button';
import { Cctv, Monitor, Package, ChevronRight, PcCase, Computer, LaptopMinimal, ScanEye, Webcam, Palette } from 'lucide-react';
import ShinyText from '../ui/reactbits/ShinyText.tsx';
import IconMorph from '../ui/IconMorph';
import type { IconMorphHandle } from '../ui/IconMorph';
import { useServiceForm } from '../../contexts/ServiceFormContext';
import { useContactForm } from '../../contexts/ContactFormContext';

// Types for structured pricing content
type CategoryKey = 'cctv' | 'web' | 'bundle';

interface Card {
  name: string;
  price?: string; // e.g., ₹30,000 or ₹1,500 – ₹2,000
  oldPrice?: string; // e.g., compare-at price shown struck-through
  period?: string; // e.g., mo, year 1, setup
  description?: string; // small blurb under title
  features?: string[]; // bullet points
  badge?: string; // e.g., Recommended, Best Value
  recommended?: boolean; // highlight card
}

interface Category {
  title: string;
  subtitle?: string;
  sharedFeatures?: string[]; // features common to all cards in this category
  cards: Card[];
}

const CATEGORIES: { key: CategoryKey; label: string }[] = [
  { key: 'cctv', label: 'CCTV Solutions' },
  { key: 'web', label: 'Website Development' },
  { key: 'bundle', label: 'NGO Empowerment Bundle' },
];

const ICONS: Record<CategoryKey, React.ComponentType<{ className?: string }>> = {
  cctv: Cctv,
  web: Monitor,
  bundle: Package,
};

// Icon sequences for animated category icons
const ICON_SEQUENCES: Record<CategoryKey, React.ReactElement[]> = {
  cctv: [
    <Cctv key="cctv-1" />, <ScanEye key="scaneye" />, <Webcam key="webcam" />, <Cctv key="cctv-2" />
  ],
  web: [
    <PcCase key="pc-1" />, <Computer key="computer" />, <LaptopMinimal key="laptop" />, <PcCase key="pc-2" />
  ],
  bundle: [
    <Package key="pkg-1" />, <Package key="pkg-2" />
  ],
};

// Single source of truth for all pricing content
const DATA: Record<CategoryKey, Category> = {
  cctv: {
    title: 'CCTV Solutions',
    subtitle: 'Flexible options to fit your needs',
    sharedFeatures: [
      'No Port forwarding',
      'Installation & Training',
      '6+ Cameras, Website Embed',
      'Password-Protected Viewing',
      'End-to-End Encryption',
      'Add More Cameras Anytime',
    ],
    cards: [
      {
        name: 'CCTV Stream-to-Web',
        price: '₹15,000',
        description: 'Total One Time Investment',
        features: [
          'Hardware + Setup (onsite/online): Included',
          'Secure Server Setup',
          'Password Protection',
          'Embeddable link',
          'Special Notes: Limited offer for first 25 NGOs / Non-Profits',
        ],
        badge: 'Recommended',
        recommended: true,
      },
    ],
  },
  web: {
    title: 'Website Development',
    subtitle: 'Modern, secure, headless websites (no WordPress headaches)',
    cards: [
      {
        name: 'Static Presence (Simple one-page site)',
        price: '₹8,000',
        features: [
          'Clean single-page design',
          'Fast load speeds',
          'Best for portfolios, personal sites, or small local businesses',
        ],
      },
      {
        name: 'Professional CMS (For NGOs, SMBs, normal orgs, etc)',
        price: '₹24,599',
        features: [
          'Blog-style multi-page website',
          'Donation page / Lead forms (as needed)',
          'Event calendar / Bookings (flexible add-ons)',
          'Analytics dashboard (as needed)',
          'Integrations (WhatsApp, Email, CRM, Payments) (as needed)',
          'Scales with your needs',
        ],
        badge: 'Best Value',
        recommended: true,
      },
      {
        name: 'All Website Plans Include',
      },
    ],
  },
  bundle: {
    title: 'NGO Empowerment Bundle',
    subtitle: 'Exclusive offer for Early Bird NGOs',
    cards: [
      {
        name: 'NGOs/Non-Profits Bundle',
        price: '₹54,999',
        features: [
          '₹30,000 (CCTV Stream-to-Web All‑Inclusive Package )',
          '₹24,599 (Professional CMS Website Setup)',
'12 months website maintenance FREE for the Professional CMS package'
        ],
badge: 'All‑in‑one NGO‑First',
        recommended: true,
      },
      {
        name: 'Benefits & Renewal',
        description: 'Purpose‑built for NGOs',
        features: [
          'Bundle Benefits:',
          'Best value for Early Bird NGOs',
          'All‑in‑one implementation and training',
          'No WordPress headaches (headless stack)',
          'Priority onboarding',
          '—',
          'Renewal:',
          'Bundle Offer: ₹2,699/month — combined CCTV + Community Hub',
          'Pay for 12 months, only 11 billed',
        ],
      },
    ],
  },
};

const PricingSection: React.FC = () => {
  const [active, setActive] = React.useState<CategoryKey>('cctv');
  const category = DATA[active];
  
  // Use service form context
  const { openForm } = useServiceForm();
  const { openForm: openContactForm } = useContactForm();
  
  // Handle service button clicks
  const handleServiceClick = (serviceName: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      (event.nativeEvent as Event)?.stopImmediatePropagation?.();
    }
    
    // Store current URL for restoration
    const beforeURL = window.location.href;
    
    openForm(serviceName);
    
    // Restore URL if it changed unexpectedly (e.g. hash navigation)
    setTimeout(() => {
      if (window.location.href !== beforeURL) {
        window.history.replaceState(null, '', beforeURL);
      }
    }, 0);
  };

  // Animated icon setup for category header
  const headerIconRef = React.useRef<IconMorphHandle | null>(null);
  const activeIconSequence = ICON_SEQUENCES[active];
  React.useEffect(() => {
    headerIconRef.current?.trigger();
  }, [active]);

  // Measure CCTV card height and reuse as baseline across categories
  const sectionRef = React.useRef<HTMLElement | null>(null);
  const [cctvCardHeight, setCctvCardHeight] = React.useState<number | null>(null);
  const [applyMinHeight, setApplyMinHeight] = React.useState<boolean>(false);

  // Only apply min-height on md and above for better mobile UX
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setApplyMinHeight(mq.matches);
    update();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    } else {
      // Fallback for older browsers
      // @ts-ignore
      mq.addListener(update);
      return () => {
        // @ts-ignore
        mq.removeListener(update);
      };
    }
  }, []);

  const computeCctvHeights = React.useCallback(() => {
    const root = sectionRef.current;
    if (!root) return;
    const nodes = root.querySelectorAll<HTMLElement>('[data-pricing-card="true"]');
    if (!nodes.length) return;
    let max = 0;
    nodes.forEach((el) => {
      max = Math.max(max, el.offsetHeight);
    });
    setCctvCardHeight(max);
  }, []);

  // Measure when CCTV is active (default state)
  React.useEffect(() => {
    if (active !== 'cctv') return;
    const id = window.requestAnimationFrame(() => computeCctvHeights());
    return () => window.cancelAnimationFrame(id);
  }, [active, computeCctvHeights]);

  // Recompute on resize while CCTV is selected
  React.useEffect(() => {
    const onResize = () => {
      if (active === 'cctv') computeCctvHeights();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [active, computeCctvHeights]);

  // Render card title with smaller parentheses kept on a single line
  const renderTitle = (title: string): React.ReactNode => {
    const start = title.indexOf('(');
    const end = title.lastIndexOf(')');
    if (start !== -1 && end !== -1 && end > start) {
      const main = title.slice(0, start).trim();
      const paren = title.slice(start, end + 1);
      return (
        <>
          {main}{' '}
          <span className="text-sm font-medium whitespace-nowrap">{paren}</span>
        </>
      );
    }
    // Fallback preserves existing line-break behaviour
    return title.split('\n').map((line, i, arr) => (
      <React.Fragment key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <section ref={sectionRef} id="pricing" className="relative py-24 md:py-32 section-backdrop-light">

      <div className="section-grain" aria-hidden="true" />
      <div className="container relative mx-auto px-3 sm:px-4 lg:px-6">
        {/* Header */}
        <div className="mb-12 md:mb-16">
          {/* ≤1024px: number next to the line */}
          <div className="flex items-center lg:hidden mb-3">
            <span className="font-mono text-xs tracking-widest text-foreground/60 mr-2">[ 04 ]</span>
            <div className="w-20 border-t-2 border-foreground" />
          </div>
          {/* >1024px: original layout */}
          <div className="hidden lg:flex flex-row items-baseline">
            <span className="font-mono text-xs tracking-widest text-foreground/60 mr-6">[ 04 ]</span>
            <div>
              <div className="w-20 border-t-2 border-foreground mb-4" />
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-extrabold leading-tight">
                Our Pricing
              </h2>
            </div>
          </div>
          {/* ≤1024px title below */}
          <div className="lg:hidden">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-extrabold leading-tight">Our Pricing</h2>
          </div>
        </div>

        {/* Overview rows: horizontal cards with CTA on the right */}
        <div className="grid grid-cols-12 gap-6 max-w-5xl ml-auto mb-10">
          <div className="col-span-12 space-y-4 md:space-y-5">
            {/* CCTV Streaming */}
            <div className="group glow-card card-backdrop relative border-2 p-4 md:p-6 animate-on-scroll transition-transform duration-200 hover:md:scale-[1.02] active:scale-[0.98] border-foreground">
              {/* Early Bird Offer Tags */}
              <div className="absolute -top-3 left-4 md:left-6">
                <span className="inline-flex items-center px-3 py-1 text-xs font-bold bg-amber-500 text-white rounded-full animate-pulse">
                  🚀 EARLY BIRD OFFER
                </span>
              </div>
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-12 md:col-span-9 lg:col-span-9">
                  <div className="flex items-start gap-3">
                    <Cctv className="h-7 w-7 md:h-8 md:w-8 text-foreground/80" strokeWidth={1.4} aria-hidden="true" />
                    <div>
                      <h3 className="text-lg md:text-xl font-bold">CCTV Stream Server — No Port Forwarding Required</h3>
                      <p className="text-sm text-foreground/80 mt-0.5">Secure, proprietary streaming for your website. Plug-and-play setup on your hardware or ours. No network holes, no security risks—just a reliable feed. <a href="/CCTV-Stream-Server" target="_blank" rel="noopener noreferrer" className="font-semibold underline hover:text-primary transition-colors">Learn more</a></p>
                      <div className="inline-flex items-baseline gap-2 whitespace-nowrap font-display font-extrabold text-3xl md:text-4xl mt-2">
                        <ShinyText text="₹1,500" speed={1.8} className="text-foreground" />
                        <span className="text-xs md:text-sm font-normal text-foreground/60"><span className="text-sm md:text-base text-foreground/70">Starting</span> / Month</span>
                      </div>
                      <p className="text-xs md:text-sm text-foreground/60 mt-1">₹7,000 Setup Fee • Custom Hardware Options • Free SSL • <span className="font-medium text-foreground/80">(₹2,360/mo after early bird offer ends)</span></p>
                      {/* <div className="mt-2">
                        <a href="#demo-cctv" className="text-sm font-semibold link-underline">➡ Check Demo</a>
                      </div> */}
                    </div>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-3 lg:col-span-3 flex md:justify-end">
                  <button 
                    type="button"
                    className="group inline-flex items-center justify-center font-bold tracking-wider uppercase transition-all duration-300 focus-ring disabled:pointer-events-none disabled:opacity-50 relative overflow-hidden text-sm px-6 py-3 bg-foreground border-2 border-foreground text-background hover:bg-primary hover:text-primary-foreground hover:border-primary w-full md:w-auto"
                    onClick={(e) => handleServiceClick('CCTV Streaming', e)}
                  >
                    Request Setup
                  </button>
                </div>
              </div>
            </div>

            {/* Websites that Grow with You */}
            <div className="group glow-card relative border-2 p-4 md:p-6 animate-on-scroll transition-transform duration-200 hover:md:scale-[1.02] active:scale-[0.98] border-foreground card-backdrop">
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-12 md:col-span-9 lg:col-span-9">
                  <div className="flex items-start gap-3">
                    <Monitor className="h-7 w-7 md:h-8 md:w-8 text-foreground/80" strokeWidth={1.4} aria-hidden="true" />
                    <div>
                      <h3 className="text-lg md:text-xl font-bold">Professional Websites — Built to Grow</h3>
                      <p className="text-sm text-foreground/80 mt-0.5">Modern, secure sites for local shops, cafes, NGOs, and businesses. We handle everything from simple blogs to full online stores and custom dashboards.</p>
                      <div className="inline-flex items-baseline gap-2 whitespace-nowrap font-display font-extrabold text-3xl md:text-4xl mt-2">
                        <ShinyText text="₹8,000" speed={1.8} className="text-foreground" />
                        <span className="text-xs md:text-sm font-normal text-foreground/60"><span className="text-sm md:text-base text-foreground/70">Starting</span></span>
                      </div>
                      <p className="text-xs md:text-sm text-foreground/60 mt-1">Free Hosting & SSL Included • Single-Page Base • Unlimited Uploads</p>
                      {/* <div className="mt-2">
                        <a href="#samples-web" className="text-sm font-semibold link-underline">➡ View Samples</a>
                      </div> */}
                    </div>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-3 lg:col-span-3 flex md:justify-end">
                  <Button 
                    variant="primary" 
                    className="w-full md:w-auto uppercase" 
                    type="button"
                    onClick={(e) => handleServiceClick('Websites that Grow with You', e)}
                  >
                    Inquire Now
                  </Button>
                </div>
              </div>
            </div>

            {/* Design & Branding - Starter Brand Kit */}
            <div className="group glow-card relative border-2 p-4 md:p-6 animate-on-scroll transition-transform duration-200 hover:md:scale-[1.02] active:scale-[0.98] border-foreground card-backdrop">
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-12 md:col-span-9 lg:col-span-9">
                  <div className="flex items-start gap-3">
                    <Palette className="h-7 w-7 md:h-8 md:w-8 text-foreground/80" strokeWidth={1.4} aria-hidden="true" />
                    <div>
                      <h3 className="text-lg md:text-xl font-bold">Brand Identity & Visual Systems</h3>
                      <p className="text-sm text-foreground/80 mt-0.5">A professional visual identity built to set you apart. We craft your total brand ecosystem—including logo systems, style guides, and media presence for your website and social platforms.</p>
                      <div className="inline-flex items-baseline gap-2 whitespace-nowrap font-display font-extrabold text-3xl md:text-4xl mt-2">
                        <ShinyText text="₹12,000" speed={1.8} className="text-foreground" />
                        <span className="text-xs md:text-sm font-normal text-foreground/60"><span className="text-sm md:text-base text-foreground/70">Initial Investment</span></span>
                      </div>
                      <p className="text-xs md:text-sm text-foreground/60 mt-1">Full Logo Systems • Digital Style Guides • Media Presence • UI/UX Design</p>
                      {/* <div className="mt-2">
                        <a href="#samples-branding" className="text-sm font-semibold link-underline">➡ View Portfolio</a>
                      </div> */}
                    </div>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-3 lg:col-span-3 flex md:justify-end">
                  <Button 
                    variant="primary" 
                    className="w-full md:w-auto uppercase" 
                    type="button"
                    onClick={(e) => handleServiceClick('Starter Brand Kit & More', e)}
                  >
                    Start Project
                  </Button>
                </div>
              </div>
            </div>

            {/* CCTV + Website Bundle */}
            <div className="group glow-card relative border-2 p-4 md:p-6 animate-on-scroll transition-transform duration-200 hover:md:scale-[1.02] active:scale-[0.98] border-foreground card-backdrop">
              {/* BEST DEAL Badge */}
              <div className="absolute -top-3 left-4 md:left-6">
                <span className="inline-flex items-center px-3 py-1 text-xs font-bold bg-primary text-primary-foreground rounded-full animate-pulse">
                  🔥 BEST DEAL
                </span>
              </div>
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-12 md:col-span-9 lg:col-span-9">
                  <div className="flex items-start gap-3">
                    <Package className="h-7 w-7 md:h-8 md:w-8 text-foreground/80" strokeWidth={1.4} aria-hidden="true" />
                    <div>
                      <h3 className="text-lg md:text-xl font-bold">The Complete Digital Foundation for NGOs</h3>
                      <p className="text-sm text-foreground/80 mt-0.5">Everything your organization needs to build trust and presence. We provide a professional website, secure CCTV streaming, and a complete brand identity in one seamless, managed setup.</p>
                      <div className="inline-flex items-baseline gap-2 whitespace-nowrap font-display font-extrabold text-3xl md:text-4xl mt-2">
                        <ShinyText text="₹15,000" speed={1.8} className="text-foreground" />
                        <span className="text-xs md:text-sm font-normal text-foreground/60">Initial Investment</span>
                      </div>
                      <p className="text-xs md:text-sm text-foreground/60 mt-1">Professional Website • Secure CCTV Stream • Full Brand Identity • Priority Support • ₹1,500 / month <span className="font-medium text-foreground/80">(₹2,360/mo after early bird offer ends)</span></p>
                      {/* <div className="mt-2">
<a href="#demo-bundle" className="text-sm font-semibold link-underline">➡ Check Demo</a>
                      </div> */}
                    </div>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-3 lg:col-span-3 flex md:justify-end">
                  <div className="relative w-full md:w-auto">
                    {/* Animated gradient border */}
                    <div 
                      className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_100%] animate-gradient-shift opacity-75"
                      style={{
                        padding: '2px',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude'
                      }}
                    />
                    <Button 
                      variant="primary" 
                      className="relative w-full md:w-auto uppercase" 
                      type="button"
                      onClick={(e) => handleServiceClick('CCTV + Website Starter Bundle', e)}
                    >
                      Get the Bundle
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Previous detailed pricing grid removed */}
        <div className="hidden">
          {/* Content spans full grid; left negative space is created by ml-auto and max-width */}
          <div className="col-span-12">
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex items-center gap-2 cursor-pointer" onMouseEnter={() => headerIconRef.current?.trigger()} onFocus={() => headerIconRef.current?.trigger()}>
                  <IconMorph
                    ref={headerIconRef}
                    triggerMode="external"
                    icons={activeIconSequence}
                    size={36}
                    strokeWidth={1.4}
                    stepDurationMs={600}
                    transitionStyle="motion"
                    className="text-foreground/80"
                  />
                  <h3 className="text-2xl md:text-3xl font-display font-bold">{category.title}</h3>
                </div>
                <div className="-mx-1 md:mx-0 w-full md:w-auto">
                  <div className="flex overflow-x-auto hide-scrollbar gap-2 px-1" role="navigation" aria-label="Switch category">
                    {CATEGORIES.filter((c) => c.key !== active).map((c) => {
                      const PillIcon = ICONS[c.key as CategoryKey];
                      return (
                        <button
                          key={c.key}
                          type="button"
                          title={`View ${c.label}`}
                          aria-label={`View ${c.label}`}
                          onClick={() => setActive(c.key as CategoryKey)}
                          className="group flex items-center gap-2 whitespace-nowrap rounded-full border-2 px-3 py-1.5 text-sm cursor-pointer transition-colors border-foreground/40 text-foreground/80 hover:text-foreground hover:border-foreground/80 bg-secondary/40 hover:bg-secondary focus-ring"
                        >
                          <PillIcon className="h-4 w-4" aria-hidden="true" />
                          <span className="link-underline">{c.label}</span>
                          <ChevronRight className="h-4 w-4 opacity-70 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              {category.subtitle && (
                <p className="mt-2 text-sm text-foreground/70">{category.subtitle}</p>
              )}
            </div>

            {/* Mobile/Tablet: horizontal, swipeable cards with peek (visible up to <1024px) */}
            <div className="block lg:hidden -mx-3 px-3">
              <div className="flex gap-4 overflow-x-auto overflow-y-hidden hide-scrollbar snap-x snap-mandatory">
                {category.cards.map((card, index) => (
                  <div key={index} className="min-w-[58%] sm:min-w-[50%] md:min-w-[45%] max-w-[22rem] flex-shrink-0 snap-center">
                    <div
                      data-pricing-card="true"
                      className={`group glow-card relative border-2 p-6 animate-on-scroll transition-transform duration-200 h-full flex flex-col ${(card.price ? 'hover:md:scale-[1.02]' : '')} ${
                        card.recommended
                          ? 'border-primary bg-secondary'
                          : 'border-foreground card-backdrop'
                      }`}
                      data-delay={`${index * 0.1}s`}
                    >
                      {card.badge ? (
                        card.recommended ? (
                          <div className="-mx-6 -mt-6 mb-4 px-6 py-3 text-xs font-bold uppercase text-center bg-primary text-primary-foreground">
                            {card.badge}
                          </div>
                        ) : (
                          <div className="-mx-6 -mt-6 mb-4 px-6 py-3 text-xs font-bold uppercase text-center bg-secondary text-foreground">
                            {card.badge}
                          </div>
                        )
                      ) : (
                        active === 'web' && card.name.startsWith('Static Presence') ? (
                          <div className="-mx-6 -mt-6 mb-4 px-6 py-3 text-xs font-bold uppercase text-center bg-primary text-primary-foreground opacity-0 select-none">Best Value</div>
                        ) : null
                      )}

                      <div className="text-left mb-5">
                        {!(card.name === 'All Website Plans Include' || card.name === 'Benefits & Renewal') && (
                          <>
                            <h4 className="text-xl font-bold mb-2">{renderTitle(card.name)}</h4>
                            {active === 'cctv' && (card.recommended || card.name.startsWith('CCTV Stream-to-Web')) && (
                              <p className="text-sm text-foreground/60">One-time setup starts at</p>
                            )}
                            {active === 'web' && card.name.startsWith('Static Presence') && (
                              <p className="text-sm text-foreground/60">One-time setup starts at</p>
                            )}
                            {active === 'web' && card.name.startsWith('Professional CMS') && (
                              <p className="text-sm text-foreground/60">One-time setup starts at</p>
                            )}
                            {active === 'bundle' && card.name.startsWith('NGOs/Non-Profits Bundle') && (
                              <p className="text-sm text-foreground/60">One-time setup starts at</p>
                            )}
                            {(card.price || card.period) && (
                              <div className="inline-flex items-baseline gap-2 whitespace-nowrap font-display font-extrabold text-3xl mb-1">
                                {card.price && (
                                  <ShinyText text={card.price} speed={1.8} className="text-foreground" />
                                )}
                                {card.oldPrice && (
                                  <span className="text-base font-normal text-foreground/60 line-through">{card.oldPrice}</span>
                                )}
                                {card.period && (
                                  <span className="text-sm font-normal text-foreground/60 whitespace-nowrap">/{card.period}</span>
                                )}
                              </div>
                            )}
                            {card.description && !(active === 'cctv' && (card.recommended || card.name.startsWith('CCTV Stream-to-Web'))) && (
                              <p className="text-sm text-foreground/70">{card.description}</p>
                            )}
                          </>
                        )}
                      </div>

                      {card.name === 'Benefits & Renewal' || card.name === 'All Website Plans Include' ? (
                        card.name === 'All Website Plans Include' ? (
                          <div className="text-left mb-5">
                            <h4 className="text-xl font-bold mb-3">This Plans Include</h4>
                            <ul className="space-y-2 text-sm mb-4 list-none pl-0">
                              <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>SEO optimization</span></li>
                              <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Hosting + SSL (secure & reliable)</span></li>
                              <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Mobile-friendly responsive design</span></li>
                              <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Modern, optimized layouts</span></li>
                              <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Priority email support</span></li>
                              <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Basic maintenance & security updates</span></li>
                            </ul>
                            <div className="border-t border-foreground/10 my-3" />
                            <h4 className="text-xl font-bold mb-3">Renewal / From Next Year</h4>
                            <ul className="space-y-2 text-sm">
                              <li><span className="font-semibold">Static Presence:</span> ₹500/month — payable in 6 or 12-month advance</li>
                              <li><span className="font-semibold">Professional CMS:</span> ₹2,000/month — payable in 6 or 12-month advance</li>
                              <li className="text-xs text-foreground/70">If paid 12 months in advance, pay only for 11 months.</li>
                            </ul>
                          </div>
                        ) : (
                          <div className="text-left mb-5">
                            <h4 className="text-xl font-bold mb-1">Benefits</h4>
                            <p className="text-sm text-foreground/70 mb-2">Purpose‑built for NGOs</p>
                            <ul className="space-y-2 text-sm mb-4 list-none pl-0">
                              <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Best value for Early Bird NGOs</span></li>
                              <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>All‑in‑one implementation and training</span></li>
                              <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>No WordPress headaches (headless stack)</span></li>
                              <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Priority onboarding</span></li>
                            </ul>
                            <div className="border-t border-foreground/10 my-3" />
                            <h4 className="text-xl font-bold mb-3">Renewal</h4>
                            <ul className="space-y-2 text-sm">
                              <li><span className="font-semibold">Bundle Offer:</span> ₹2,699/month — combined CCTV + Professional CMS</li>
                              <li className="text-xs text-foreground/70">If paid 12 months in advance, pay only for 11 months.</li>
                            </ul>
                          </div>
                        )
                      ) : (
                        <>
                          {(() => {
                            const isSave = (s: string) => s.trim().startsWith('Save ₹');
                            const visible = Array.isArray(card.features)
                              ? card.features.filter((f) => !isSave(f))
                              : [];
                            return visible.length > 0 ? (
                              <ul className="space-y-3 mb-6">
                                {visible.map((feature, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <span className="text-primary mr-2 mt-0.5">✓</span>
                                    <span className="text-sm">{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : null;
                          })()}


                        </>
                      )}


{!(card.name === 'Benefits & Renewal' || card.name === 'All Website Plans Include') && (
                        <div className="mt-auto">
                          {(() => {
                            const isSave = (s: string) => s.trim().startsWith('Save ₹');
                            let note: React.ReactNode | undefined;
                            if (active === 'cctv' && (card.recommended || card.name.startsWith('CCTV Stream-to-Web'))) {
                              note = 'Save ₹9,000 with year‑1 upfront: ₹30,000 all‑inclusive (hardware, setup, maintenance) — only for first 25 NGOs / Non-Profits.';
                            } else if (active === 'web') {
                              if (card.name.startsWith('Static Presence')) {
                                note = (
                                  <>
                                    Save ₹2,000 with year‑1 upfront: ₹12,000 (<span className="line-through">₹14,000</span>) all‑inclusive (setup, maintenance and other)
                                  </>
                                );
                              } else if (card.name.startsWith('Professional CMS')) {
                                note = (
                                  <>
                                    Save ₹6,599 with year‑1 upfront: ₹42,000 (<span className="line-through">₹48,599</span>) all‑inclusive (setup, maintenance and other)
                                  </>
                                );
                              } else if (Array.isArray(card.features)) {
                                note = card.features.find((f) => isSave(f));
                              }
                            } else if (active === 'bundle' && card.name.startsWith('NGOs/Non-Profits Bundle')) {
                              note = (
                                <>
                                  Save ₹32,600 with year‑1 upfront: ₹54,999 (<span className="line-through">₹87,599</span>) all‑inclusive (hardware, setup, maintenance) — only for first 25 NGOs / Non-Profits
                                </>
                              );
                            }
                            return note ? (
                              <p className="text-xs text-foreground/70 mb-3">
                                <span
                                  className="shiny-text-hover inline-block text-foreground"
                                  style={{
                                    backgroundImage:
                                      'linear-gradient(90deg, #000000 0%, #000000 30%, #00ffff 50%, #000000 70%, #000000 100%)',
                                    backgroundSize: '200% 100%',
                                    WebkitBackgroundClip: 'text',
                                    backgroundClip: 'text',
                                  }}
                                >
                                  💡 {note}
                                </span>
                              </p>
                            ) : null;
                          })()}
                          <Button
                            variant="primary"
                            className="w-full py-4"
                            asLink
                            href="#contact"
                          >
                            {active === 'web' && card.period === 'setup' ? 'Start Setup' : 'Book Us'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {active === 'cctv' && (
                  <div className="min-w-[58%] sm:min-w-[50%] md:min-w-[45%] max-w-[22rem] flex-shrink-0 snap-center">
                    <div className="glow-card relative border-2 p-6 animate-on-scroll transition-transform duration-200 h-full flex flex-col border-foreground card-backdrop" data-delay="0.15s">
                      <div className="text-left mb-5">
<h4 className="text-xl font-bold mb-3">This includes</h4>
                        <ul className="space-y-2 text-sm mb-4">
                          {category.sharedFeatures?.map((feat, i) => (
                            <li key={i} className="flex items-start">
                              <span className="text-primary mr-2 mt-0.5">✓</span>
                              <span>{feat}</span>
                            </li>
                          ))}
                        </ul>
                        <div className="border-t border-foreground/10 my-3" />
                        <h4 className="text-xl font-bold mb-3">Renewal / From Next Year</h4>
                        <ul className="space-y-2 text-sm">
<li><span className="font-semibold">NGOs & Non-Profits Early Bird:</span> <span className="font-semibold">₹1,500/month</span> (<span className="line-through">₹2,000/month</span>) — payable in 6 or 12-month advance</li>
                          <li><span className="font-semibold">Businesses & Professionals:</span> <span className="font-semibold">₹2,300/month</span> — payable in 6 or 12-month advance</li>
                        </ul>
                        <p className="text-xs text-foreground/70 mt-3">If paid 12 months in advance, pay only for 11 months.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Desktop grid (≥1024px) */}
            <div className="hidden lg:grid lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
              {category.cards.map((card, index) => (
                <div
                  key={index}
                  data-pricing-card="true"
                      className={`group glow-card relative border-2 p-6 md:p-8 animate-on-scroll transition-transform duration-200 h-full flex flex-col ${(card.price ? 'hover:md:scale-[1.02]' : '')} ${
                    card.recommended
                      ? 'border-primary bg-secondary'
                      : 'border-foreground card-backdrop'}${((active === 'bundle' || active === 'cctv') && index === 0) ? ' lg:col-start-2' : ''}`}
                  style={applyMinHeight && cctvCardHeight ? { minHeight: cctvCardHeight } : undefined}
                  data-delay={`${index * 0.1}s`}
                >
                  {card.badge ? (
                    <div
                      className={`absolute -top-4 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-bold uppercase ${
                        card.recommended
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-foreground/20 text-foreground border border-foreground/30'
                      }`}
                    >
                      {card.badge}
                    </div>
                  ) : (
                    active === 'web' && card.name.startsWith('Static Presence') ? (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-bold uppercase bg-primary text-primary-foreground opacity-0 select-none">Best Value</div>
                    ) : null
                  )}

                  <div className="text-left mb-5 md:mb-6">
                    {!(card.name === 'All Website Plans Include' || card.name === 'Benefits & Renewal') && (
                      <>
                        <h4 className="text-xl md:text-2xl font-bold mb-2">{renderTitle(card.name)}</h4>
                        {active === 'cctv' && (card.recommended || card.name.startsWith('CCTV Stream-to-Web')) && (
                          <p className="text-sm text-foreground/60">One-time setup starts at</p>
                        )}
                        {active === 'web' && card.name.startsWith('Static Presence') && (
                          <p className="text-sm text-foreground/60">One-time setup starts at</p>
                        )}
                        {active === 'web' && card.name.startsWith('Professional CMS') && (
                      <p className="text-sm text-foreground/60">One-time setup starts at</p>
                    )}
                    {active === 'bundle' && card.name.startsWith('NGOs/Non-Profits Bundle') && (
                      <p className="text-sm text-foreground/60">One-time setup starts at</p>
                    )}
                        {(card.price || card.period) && (
                          <div className="inline-flex items-baseline gap-2 whitespace-nowrap font-display font-extrabold text-3xl md:text-4xl mb-1">
                            {card.price && (
                              <ShinyText text={card.price} speed={1.8} className="text-foreground" />
                            )}
                            {card.oldPrice && (
                              <span className="text-base font-normal text-foreground/60 line-through">{card.oldPrice}</span>
                            )}
                            {card.period && (
                              <span className="text-sm font-normal text-foreground/60">/{card.period}</span>
                            )}
                          </div>
                        )}
                        {card.description && !(active === 'cctv' && (card.recommended || card.name.startsWith('CCTV Stream-to-Web'))) && (
                          <p className="text-sm text-foreground/70">{card.description}</p>
                        )}
                      </>
                    )}
                  </div>

                  {card.name === 'Benefits & Renewal' || card.name === 'All Website Plans Include' ? (
                    card.name === 'All Website Plans Include' ? (
                      <div className="text-left mb-5 md:mb-6">
                        <h4 className="text-xl md:text-2xl font-bold mb-3">This Plans Include</h4>
                        <ul className="space-y-2 text-sm mb-4 list-none pl-0">
                          <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>SEO optimization</span></li>
                          <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Hosting + SSL (secure & reliable)</span></li>
                          <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Mobile-friendly responsive design</span></li>
                          <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Modern, optimized layouts</span></li>
                          <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Priority email support</span></li>
                          <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Basic maintenance & security updates</span></li>
                        </ul>
                        <div className="border-t border-foreground/10 my-3" />
                        <h4 className="text-xl md:text-2xl font-bold mb-3">Renewal / From Next Year</h4>
                        <ul className="space-y-2 text-sm">
                          <li><span className="font-semibold">Static Presence:</span> ₹500/month — payable in 6 or 12-month advance</li>
                          <li><span className="font-semibold">Professional CMS:</span> ₹2,000/month — payable in 6 or 12-month advance</li>
                          <li className="text-xs text-foreground/70">If paid 12 months in advance, pay only for 11 months.</li>
                        </ul>
                      </div>
                    ) : (
                      <div className="text-left mb-5 md:mb-6">
                        <h4 className="text-xl md:text-2xl font-bold mb-1">Benefits</h4>
                        <p className="text-sm text-foreground/70 mb-2">Purpose‑built for NGOs</p>
                        <ul className="space-y-2 text-sm mb-4 list-none pl-0">
                          <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Best value for Early Bird NGOs</span></li>
                          <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>All‑in‑one implementation and training</span></li>
                          <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>No WordPress headaches (headless stack)</span></li>
                          <li className="flex items-start"><span className="text-primary mr-2 mt-0.5">✓</span><span>Priority onboarding</span></li>
                        </ul>
                        <div className="border-t border-foreground/10 my-3" />
                        <h4 className="text-xl md:text-2xl font-bold mb-3">Renewal</h4>
                        <ul className="space-y-2 text-sm">
                          <li><span className="font-semibold">Bundle Offer:</span> ₹2,699/month — combined CCTV + Professional CMS</li>
                          <li className="text-xs text-foreground/70">If paid 12 months in advance, pay only for 11 months.</li>
                        </ul>
                      </div>
                    )
                  ) : (
                    <>
                      {(() => {
                        const isSave = (s: string) => s.trim().startsWith('Save ₹');
                        const visible = Array.isArray(card.features)
                          ? card.features.filter((f) => !isSave(f))
                          : [];
                        return visible.length > 0 ? (
                          <ul className="space-y-3 mb-6">
                            {visible.map((feature, idx) => (
                              <li key={idx} className="flex items-start">
                                <span className="text-primary mr-2 mt-0.5">✓</span>
                                <span className="text-sm">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        ) : null;
                      })()}


                    </>
                  )}


{!(card.name === 'Benefits & Renewal' || card.name === 'All Website Plans Include') && (
                    <div className="mt-auto">
                      {(() => {
                        const isSave = (s: string) => s.trim().startsWith('Save ₹');
                        let note: React.ReactNode | undefined;
                        if (active === 'cctv' && (card.recommended || card.name.startsWith('CCTV Stream-to-Web'))) {
                          note = 'Save ₹9,000 with year‑1 upfront: ₹30,000 all‑inclusive (hardware, setup, maintenance) — only for first 25 NGOs / Non-Profits.';
                        } else if (active === 'web') {
                          if (card.name.startsWith('Static Presence')) {
                            note = (
                              <>
                                Save ₹2,000 with year‑1 upfront: ₹12,000 (<span className="line-through">₹14,000</span>) all‑inclusive (setup, maintenance and other)
                              </>
                            );
                          } else if (card.name.startsWith('Professional CMS')) {
                            note = (
                              <>
                                Save ₹6,599 with year‑1 upfront: ₹42,000 (<span className="line-through">₹48,599</span>) all‑inclusive (setup, maintenance and other)
                              </>
                            );
                          } else if (Array.isArray(card.features)) {
                            note = card.features.find((f) => isSave(f));
                          }
                        } else if (active === 'bundle' && card.name.startsWith('NGOs/Non-Profits Bundle')) {
                          note = (
                            <>
                              Save ₹32,600 with year‑1 upfront: ₹54,999 (<span className="line-through">₹87,599</span>) all‑inclusive (hardware, setup, maintenance) — only for first 25 NGOs / Non-Profits
                            </>
                          );
                        }
                        return note ? (
                          <p className="text-xs text-foreground/70 mb-3 shiny-note-wrapper">
                            <span
                              className="shiny-text-hover inline-block text-foreground"
                              style={{
                                backgroundImage:
                                  'linear-gradient(90deg, #000000 0%, #000000 30%, #00ffff 50%, #000000 70%, #000000 100%)',
                                backgroundSize: '200% 100%',
                                WebkitBackgroundClip: 'text',
                                backgroundClip: 'text',
                              }}
                            >
                              💡 {note}
                            </span>
                          </p>
                        ) : null;
                      })()}
                      <Button
                        variant="primary"
                        className="w-full py-4 md:py-3"
                        asLink
                        href="#contact"
                      >
                        {active === 'web' && card.period === 'setup' ? 'Start Setup' : 'Book Us'}
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              {active === 'cctv' && (
                <div className="group glow-card relative border-2 p-6 md:p-8 animate-on-scroll transition-transform duration-200 h-full flex flex-col border-foreground card-backdrop" data-delay="0.15s">
                  <div className="text-left mb-5 md:mb-6">
<h4 className="text-xl md:text-2xl font-bold mb-3">This includes</h4>
                    <ul className="space-y-2 text-sm mb-4">
                      {category.sharedFeatures?.map((feat, i) => (
                        <li key={i} className="flex items-start">
                          <span className="text-primary mr-2 mt-0.5">✓</span>
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="border-t border-foreground/10 my-3" />
                    <h4 className="text-xl md:text-2xl font-bold mb-3">Renewal / From Next Year</h4>
                    <ul className="space-y-2 text-sm">
<li><span className="font-semibold">NGOs & Non-Profits Early Bird:</span> <span className="font-semibold">₹1,500/month</span> (<span className="line-through">₹2,000/month</span>) — payable in 6 or 12-month advance</li>
                      <li><span className="font-semibold">Businesses & Professionals:</span> <span className="font-semibold">₹2,300/month</span> — payable in 6 or 12-month advance</li>
                    </ul>
                    <p className="text-xs text-foreground/70 mt-3">If paid 12 months in advance, pay only for 11 months.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Shared features (moved below cards) */}
{category.sharedFeatures && category.sharedFeatures.length > 0 && active !== 'cctv' && (
              <div className="mt-6 md:mt-8 glow-card border-2 border-foreground card-backdrop p-4">
                <p className="text-sm font-semibold mb-3">All plans include:</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                  {category.sharedFeatures.map((feat, i) => (
                    <li key={i} className="flex items-start">
                      <span className="mr-2">✓</span>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}


{false && (
              <div className="mt-6 md:mt-8 glow-card border-2 border-foreground card-backdrop p-6 animate-on-scroll" data-delay="0.35s">
                <h4 className="text-lg font-bold mb-3">Renewal / From Next Year</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <span className="font-semibold">Commercial Pro:</span> ₹2,000/month — payable in 6 or 12-month advance
                  </li>
                  <li>
                    <span className="font-semibold">Community Hub Early Bird:</span> ₹1,500/month — payable in 6 or 12-month advance
                  </li>
                </ul>
                <p className="text-xs text-foreground/70 mt-3">If paid 12 months in advance, pay only for 11 months.</p>
              </div>
            )}

            <div className="mt-10 md:mt-12 text-center animate-on-scroll" data-delay="0.4s">
              <p className="text-base text-foreground/60">
                <span>
    <strong>Not sure what you need?</strong> Book a Consultation for a personalized assessment.
  </span>            </p>
            </div>

            {/* Contact block visible below overview */}
            <div className="mt-10 md:mt-12 text-center animate-on-scroll" data-delay="0.4s">
              <p className="text-base text-foreground/60">
                Need something custom?{' '}
                <a
                  href="#contact"
                  className="font-bold text-black hover:underline inline-block text-base md:text-lg"
                  style={{
                    backgroundImage: 'linear-gradient(90deg, #000000 0%, #000000 30%, #00ffff 50%, #000000 70%, #000000 100%)',
                    backgroundSize: '200% 100%',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent',
                    animation: 'shine-sweep 2.5s linear infinite',
                  }}
                >
                  Book a Consultation
                </a> for a personalized quote.
              </p>
            </div>
          </div>
        </div>

        {/* Contact block visible below overview - aligned with cards above */}
        <div className="max-w-5xl ml-auto mt-10 md:mt-12">
          <div className="text-center animate-on-scroll" data-delay="0.4s">
            <p className="text-base text-foreground/60">
              Need something custom?{' '}
              <button
                onClick={() => openContactForm('Book a Consultation')}
                className="font-bold text-black hover:underline inline-block text-base md:text-lg cursor-pointer"
                style={{
                  backgroundImage: 'linear-gradient(90deg, #000000 0%, #000000 30%, #00ffff 50%, #000000 70%, #000000 100%)',
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  animation: 'shine-sweep 2.5s linear infinite',
                }}
              >
                Book a Consultation
              </button>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;

